import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Sustainability article (mapped to the "ROI" goal token) —
 * "The melt is your biggest energy bill: data-driven energy in foundries".
 * Covers melting energy intensity, holding losses and the energy/quality link,
 * with a chart of energy share by stage and a savings-potential stat callout.
 * Ties to the platform's energy-port model. Vendor-neutral; product only in the
 * closing block.
 */
export const MELT_ENERGY_ARTICLE: Article = {
  slug: "melt-energy-data-driven-foundries",
  title: "The melt is your biggest energy bill: data-driven energy in foundries",
  deck:
    "In almost every foundry, melting dominates the energy bill — and most of it is managed by feel, not by data. The waste hides in plain sight: idle holding, over-superheat, and the quiet link between energy discipline and scrap. Here is where the kilowatt-hours actually go.",
  goal: "ROI",
  readingMins: 8,
  heroStat: { value: "~50%+", label: "of foundry energy spent on melting alone" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "Energy is one of the largest costs in a foundry, and within that cost one stage swamps the rest: melting. Turning solid charge into liquid metal at well over a thousand degrees is physically the most energy-hungry thing the plant does, and it typically accounts for the majority of total energy use. Yet for all that weight on the bill, melting energy is often the least instrumented and most intuitively managed part of the operation — run by the furnace operator's experience and the production schedule, rarely by a number anyone tracks per heat. The biggest line item is also the blurriest.",
    },
    {
      kind: "paragraph",
      text:
        "That combination — large and unmeasured — is exactly where money leaks. Not in dramatic, visible waste, but in a steady drip: a furnace held hot with no metal to pour, a melt superheated further than the casting needs, a charge mix that takes longer to melt than it should. None of it shows up as an alarm. All of it shows up on the invoice. The first step to controlling it is simply to see it.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Where it goes",
      heading: "Melting dominates — everything else is a rounding error by comparison",
      sub: "You cannot manage a cost you have never broken down by stage.",
    },
    {
      kind: "chart",
      chart: "bar",
      title: "Illustrative: share of foundry energy by process stage",
      xKey: "stage",
      unit: "%",
      series: [{ dataKey: "share", label: "Share of energy (%)", tone: "warning" }],
      data: [
        { stage: "Melting", share: 55 },
        { stage: "Holding", share: 14 },
        { stage: "Heat treat", share: 12 },
        { stage: "Sand / moulding", share: 9 },
        { stage: "Compressed air", share: 6 },
        { stage: "Other", share: 4 },
      ],
      caption:
        "Generic, illustrative split — every plant differs. The shape is the lesson: melting plus holding is where the energy story is won or lost.",
    },
    {
      kind: "paragraph",
      text:
        "The chart above is illustrative, but its shape holds across foundries with remarkable consistency: melting is the giant, holding is the quiet second, and the long tail of moulding, compressed air and the rest matters far less to the energy bill than people expect. This has a blunt strategic implication. Energy-saving initiatives aimed at the tail — efficient lighting, the odd variable-speed drive — are worthwhile, but they are nibbling at the small end. The leverage is in the melt. A few percent off melting energy outweighs almost anything you could do downstream.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The quiet drain",
      heading: "Holding losses: paying to keep metal hot for nothing",
    },
    {
      kind: "paragraph",
      text:
        "After melting, the next culprit is holding. Liquid metal does not stay hot for free — a holding furnace continuously pours energy in just to fight heat loss to the surroundings. That is unavoidable in principle; metal has to be ready to pour. What is avoidable is holding metal hot longer than the schedule requires, or at a higher temperature than the next pour needs. Every minute of idle holding is energy spent producing nothing. Because holding losses are continuous and undramatic, they are easy to ignore — but over a year of shifts, idle holding time is one of the most recoverable wastes in the building, and it costs almost nothing to fix once you can see it.",
    },
    {
      kind: "metricRow",
      metrics: [
        {
          value: "Melt",
          label: "The dominant cost",
          caption: "Typically the majority of total energy — the first place to look.",
          tone: "danger",
        },
        {
          value: "Hold",
          label: "The quiet drain",
          caption: "Continuous loss while metal waits — worst when idle or over-hot.",
          tone: "warning",
        },
        {
          value: "Superheat",
          label: "The hidden surcharge",
          caption: "Every degree above what the casting needs is energy bought and wasted.",
          tone: "warning",
        },
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Over-superheat is a tax you pay twice",
      text:
        "Pushing the melt hotter than the casting requires costs energy on the way up — and can cost quality on the way down, through more gas pickup, oxidation and shrinkage. The same dial moves both the energy bill and the scrap rate.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The connection",
      heading: "Energy discipline and quality are the same problem",
      sub: "The variables that drive your energy bill drive your defects too.",
    },
    {
      kind: "paragraph",
      text:
        "Here is the insight that turns energy from a facilities concern into a process one: in a foundry, energy and quality are not separate dials. They are often the same dial. Pour temperature is the clearest example. Run too cold and you risk misruns and cold shuts; run too hot and you burn extra energy and invite gas porosity, oxidation and shrinkage. There is a window — the right temperature for the casting — and both energy and quality are best inside it. The same is true of melt timing, charge quality and holding practice. Manage them for energy and you tend to manage them for soundness; manage them for quality and you often save energy as a by-product. Treating the two as one problem is what separates a mature operation from one that optimises each in isolation and gets neither right.",
    },
    {
      kind: "pullQuote",
      quote:
        "The same degree of superheat that wastes a kilowatt-hour can also make the porosity worse. Energy and quality are not two problems — they are one, seen from two ledgers.",
      attribution: "A metallurgist's observation",
    },
    {
      kind: "paragraph",
      text:
        "Acting on any of this requires treating energy as first-class process data, not a monthly utility number that arrives weeks after the metal was poured. That means measuring energy where it is consumed — per furnace, ideally per heat — and tying each figure to the heat, the alloy and the outcome it produced. Once energy is a parameter on the same model as pour temperature and scrap, the questions become answerable: which heats cost the most energy and why; how much was spent on idle holding last week; whether the high-energy heats are also the high-scrap ones. These are not exotic analytics. They are arithmetic — but only if the energy data exists at the right grain in the first place.",
    },
    {
      kind: "statCallout",
      value: "5–15%",
      label: "melting-energy reduction is a realistic target once it is measured",
      caption:
        "Illustrative, not a guarantee. Better charge practice, less idle holding and tighter superheat control compound — and on the biggest line item, even a few percent is real money.",
      tone: "success",
    },
    {
      kind: "paragraph",
      text:
        "The prize is double. There is the direct saving — a few percent off the dominant energy cost is a material number on its own. And there is the carbon: melting energy is the bulk of a foundry's footprint, so cutting it is the single most effective lever for any sustainability or reporting commitment, far more than the visible-but-small gestures elsewhere. The same discipline that trims the bill trims the emissions. For a sector under rising pressure on both cost and carbon, the melt is where those two pressures meet — and where measuring properly pays for itself fastest.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Seeing energy by stage and tying it to quality is the goal; doing it per heat, on the same model as everything else, is the hard part. The platform treats energy as first-class process data — through energy ports on the very entities that consume it.",
      capabilities: [
        {
          title: "Energy as a modelled port",
          detail:
            "Furnaces and holders carry energy ports alongside material ports, so consumption lives on the same entity model as pour temperature and yield — not in a separate utility report.",
          icon: "plug",
        },
        {
          title: "Energy tied to the heat",
          detail:
            "Each heat's energy figure is bound to its lineage, so you can ask which heats cost the most, how much idle holding cost last week, and act per heat.",
          icon: "gauge",
        },
        {
          title: "Link energy to quality",
          detail:
            "Correlation views put energy variables next to defects, surfacing where over-superheat or holding practice is quietly driving both the bill and the scrap.",
          icon: "recycle",
        },
      ],
    },
  ],
};
